'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { getPageBackgroundColor } from '@/lib/pageColors';

// Register GSAP plugins
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

interface PageBodyBackgroundProps {
  heroId: string;
  initialColor?: string;
  targetColor?: string;
  pathname?: string;
}

/**
 * Sets the body background color and transitions to target color on scroll
 * Can be used on any page with a hero section
 */
export default function PageBodyBackground({ 
  heroId, 
  initialColor, 
  targetColor = '#FDFEF6',
  pathname: propPathname 
}: PageBodyBackgroundProps) {
  const pathname = usePathname();
  const currentPathname = propPathname || pathname;
  const scrollTriggerRef = useRef<ScrollTrigger | null>(null);

  useEffect(() => {
    // Get initial color from prop or pageColors mapping
    const startColor = initialColor || getPageBackgroundColor(currentPathname);
    
    // Only apply if we have a valid initial color (not default eggshell)
    if (startColor && startColor !== '#FDFEF6') {
      // Set initial background color
      document.body.style.backgroundColor = startColor;

      // Wait for hero element to be available
      let rafId: number | null = null;
      let ctx: gsap.Context | null = null;
      let isMounted = true;

      const initWhenReady = () => {
        if (!isMounted) return;

        const heroEl = document.getElementById(heroId);
        if (!heroEl) {
          rafId = requestAnimationFrame(initWhenReady);
          return;
        }

        if (!isMounted) return;

        // Create GSAP context for scroll-triggered background transition
        ctx = gsap.context(() => {
          gsap.to(document.body, {
            backgroundColor: targetColor,
            scrollTrigger: {
              trigger: heroEl,
              start: 'top top',
              end: '+=300px',
              scrub: 0.5,
              markers: false,
            },
            ease: 'circ.out',
          });
        });

        // Store ScrollTrigger reference for cleanup
        const triggers = ScrollTrigger.getAll();
        scrollTriggerRef.current = triggers.find(t => t.vars.trigger === heroEl) || null;
      };

      initWhenReady();

      return () => {
        isMounted = false;

        if (rafId !== null) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }

        if (ctx) {
          try {
            ctx.revert();
          } catch (error) {
            console.warn('PageBodyBackground cleanup error:', error);
          }
          ctx = null;
        }

        // Clean up ScrollTrigger
        if (scrollTriggerRef.current) {
          scrollTriggerRef.current.kill();
          scrollTriggerRef.current = null;
        }
      };
    } else {
      // Reset to default when navigating away or no custom color
      document.body.style.backgroundColor = '#FDFEF6';
    }
  }, [heroId, initialColor, targetColor, currentPathname]);

  return null;
}

